import { useCallback, useEffect, useRef, useState } from "react";
import {
  ActionIcon,
  Badge,
  Box,
  Divider,
  Group,
  Loader,
  Text,
  TextInput,
} from "@mantine/core";
import { IconSend, IconX } from "@tabler/icons-react";

/**
 * Bespoke launcher mark — a four-point "AI" spark with a small trailing
 * accent, rendered with a soft top-down white gradient for depth so it
 * reads premium rather than like a stock icon.
 */
function SparkMark({ size = 24 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="cwSparkFill" x1="12" y1="2" x2="12" y2="22">
          <stop offset="0%" stopColor="currentColor" />
          <stop offset="100%" stopColor="currentColor" stopOpacity={0.82} />
        </linearGradient>
      </defs>
      {/* Main 4-point spark: concave star drawn with quadratic curves */}
      <path
        d="M13.4 2.6c.3 3 1.1 5 2.5 6.4 1.4 1.4 3.4 2.2 6.4 2.5.5.05.5.95 0 1-3 .3-5 1.1-6.4 2.5-1.4 1.4-2.2 3.4-2.5 6.4-.05.5-.95.5-1 0-.3-3-1.1-5-2.5-6.4C8.5 13.6 6.5 12.8 3.5 12.5c-.5-.05-.5-.95 0-1 3-.3 5-1.1 6.4-2.5C11.3 7.6 12.1 5.6 12.4 2.6c.05-.5.95-.5 1 0Z"
        fill="url(#cwSparkFill)"
      />
      {/* Small accent spark, top-left */}
      <path
        d="M5.4 3.3c.15 1.2.4 1.7.9 2.2.5.5 1 .75 2.2.9.3.04.3.56 0 .6-1.2.15-1.7.4-2.2.9-.5.5-.75 1-.9 2.2-.04.3-.56.3-.6 0-.15-1.2-.4-1.7-.9-2.2-.5-.5-1-.75-2.2-.9-.3-.04-.3-.56 0-.6 1.2-.15 1.7-.4 2.2-.9.5-.5.75-1 .9-2.2.04-.3.56-.3.6 0Z"
        fill="currentColor"
        fillOpacity={0.9}
      />
    </svg>
  );
}

const BOT_BASE_URL =
  ((import.meta.env.VITE_BOT_BASE_URL as string | undefined) ??
    "http://127.0.0.1:8000") + "/api/v1/bot";
const THREAD_STORAGE_KEY = "safiri.admin.botThreadId";

function getToken(): string | null {
  return localStorage.getItem("safiri.admin.accessToken");
}

function getStoredThreadId(): string {
  return localStorage.getItem(THREAD_STORAGE_KEY) ?? "";
}

function storeThreadId(threadId: string) {
  localStorage.setItem(THREAD_STORAGE_KEY, threadId);
}

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  streaming?: boolean;
  quickReplies?: QuickReply[];
  cards?: UiCard[];
  // Card types the server says are coming (`pending` event, sent as soon as the
  // router decides and before the tools run). Empty array = text-only turn, so no
  // skeleton is shown at all. Undefined = the server hasn't decided yet.
  pendingCards?: string[];
}

type QuickReply = string | { label?: string; value?: string };
type UiCard = {
  type: string;
  presentation?: string;
  id?: string;
  data?: Record<string, unknown>;
  actions?: Array<{ label?: string; value?: string; style?: string }>;
  [key: string]: unknown;
};

const WELCOME: Message = {
  id: "welcome",
  role: "assistant",
  content:
    "Good evening. I can help you inspect trips, drivers, tickets, SOS alerts, transactions, and platform performance.",
  quickReplies: [
    "Platform overview",
    "Open support tickets",
    "Revenue this week",
    "Active SOS alerts",
  ],
};

function renderText(text: string) {
  return text.split(/(\*\*.*?\*\*)/g).map((seg, i) =>
    seg.startsWith("**") && seg.endsWith("**") ? (
      <strong key={i}>{seg.slice(2, -2)}</strong>
    ) : (
      seg
    ),
  );
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function asRows(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.map(asRecord) : [];
}

function displayValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  const record = asRecord(value);
  return displayValue(record.en ?? record.sw ?? JSON.stringify(value));
}

function titleCase(value: unknown): string {
  const text = displayValue(value);
  if (!text) return "";
  return text
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function formatMoney(value: unknown): string {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return displayValue(value);
  }
  return `TZS ${Math.round(value).toLocaleString()}`;
}

function formatGroupLabel(value: unknown): string {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const row = value as Record<string, unknown>;
    const year = displayValue(row.year ?? row.Year);
    const week = displayValue(row.week ?? row.Week);
    const month = displayValue(row.month ?? row.Month);
    const day = displayValue(row.day ?? row.Day);
    if (year && week) return `${year} W${week}`;
    if (year && month) return `${year}-${month}`;
    if (year && day) return `${year} day ${day}`;
    return Object.entries(row)
      .map(([key, val]) => `${titleCase(key)} ${displayValue(val)}`)
      .join(" · ");
  }
  return titleCase(value);
}

function analyticsValue(row: Record<string, unknown>): string {
  if (row.money !== undefined) return formatMoney(row.money);
  if (row.platformRevenue !== undefined) return formatMoney(row.platformRevenue);
  if (row.totalFare !== undefined) return formatMoney(row.totalFare);
  if (row.driverEarnings !== undefined) return formatMoney(row.driverEarnings);
  if (row.avgFare !== undefined) return formatMoney(row.avgFare);
  return displayValue(row.count ?? row.total ?? row.value ?? "");
}

function analyticsSub(row: Record<string, unknown>): string {
  const parts = [
    row.tripCount !== undefined ? `${displayValue(row.tripCount)} trips` : "",
    row.driverEarnings !== undefined ? `${formatMoney(row.driverEarnings)} driver` : "",
    row.totalFare !== undefined ? `${formatMoney(row.totalFare)} gross` : "",
  ].filter(Boolean);
  return parts.join(" · ");
}

function firstText(row: Record<string, unknown>, keys: string[]): string {
  for (const key of keys) {
    const text = displayValue(row[key]);
    if (text) return text;
  }
  return "";
}

function CardShell({
  title,
  eyebrow,
  children,
}: {
  title: string;
  eyebrow?: string;
  children: React.ReactNode;
}) {
  return (
    <Box
      style={{
        width: "100%",
        border: "1px solid rgba(24,24,27,.08)",
        borderRadius: 8,
        background: "var(--surface-default)",
        boxShadow: "0 10px 24px rgba(15,23,42,.045)",
        overflow: "hidden",
      }}
    >
      <Box style={{ padding: "10px 12px 8px" }}>
        {eyebrow && (
          <Text fz={10.5} fw={750} c="var(--text-brand)" lh={1} tt="uppercase">
            {eyebrow}
          </Text>
        )}
        <Text fw={760} fz={13} c="var(--text-primary)" lh={1.2} mt={eyebrow ? 5 : 0}>
          {title}
        </Text>
      </Box>
      <Divider color="rgba(24,24,27,.07)" />
      <Box style={{ padding: 10 }}>{children}</Box>
    </Box>
  );
}

/**
 * Placeholder that holds the card slot while a turn is in flight. Tools finish
 * before the prose is written, so the real card replaces this rather than the
 * layout jumping when text lands first.
 */
/**
 * Skeleton shaped like the card that is actually coming. The server names the
 * types in the `pending` event before the tools run, so a slow analytics query
 * shows a table outline and a wallet lookup shows a single figure — the wait
 * itself tells the admin what they are about to get.
 */
function CardSkeleton({ type }: { type?: string }) {
  const body = (() => {
    switch (type) {
      // Hero figure + stat grid + two section blocks (platform overview shape).
      case "stat_group":
        return (
          <>
            <SkBar h={52} radius={8} />
            <Box style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6, marginTop: 8 }}>
              {Array.from({ length: 6 }).map((_, i) => (
                <SkBar key={i} h={38} radius={8} />
              ))}
            </Box>
          </>
        );
      // Repeated rows: label left, value right.
      case "trip_list":
      case "user_list":
      case "ticket_list":
      case "transaction_list":
      case "lost_item_list":
      case "sos_list":
        return (
          <Box style={{ display: "grid", gap: 9 }}>
            {[92, 78, 85, 70].map((w, i) => (
              <Group key={i} justify="space-between" wrap="nowrap" gap={10}>
                <Box style={{ flex: 1 }}>
                  <SkBar w={`${w}%`} h={10} />
                  <Box mt={5}>
                    <SkBar w={`${w - 22}%`} h={8} />
                  </Box>
                </Box>
                <SkBar w={54} h={12} radius={4} />
              </Group>
            ))}
          </Box>
        );
      // Header row then tabular rows.
      case "data_table":
        return (
          <Box style={{ display: "grid", gap: 7 }}>
            <Group gap={8} wrap="nowrap">
              <SkBar w="28%" h={9} />
              <SkBar w="22%" h={9} />
              <SkBar w="22%" h={9} />
            </Group>
            {[0, 1, 2, 3].map((i) => (
              <Group key={i} gap={8} wrap="nowrap">
                <SkBar w="34%" h={11} />
                <SkBar w="18%" h={11} />
                <SkBar w="24%" h={11} />
              </Group>
            ))}
          </Box>
        );
      // Line items with a heavier total.
      case "fare_breakdown":
        return (
          <Box style={{ display: "grid", gap: 8 }}>
            {[0, 1, 2].map((i) => (
              <Group key={i} justify="space-between" wrap="nowrap">
                <SkBar w="42%" h={10} />
                <SkBar w={62} h={10} radius={4} />
              </Group>
            ))}
            <Divider color="rgba(24,24,27,.07)" />
            <Group justify="space-between" wrap="nowrap">
              <SkBar w="26%" h={12} />
              <SkBar w={78} h={14} radius={4} />
            </Group>
          </Box>
        );
      // A single large figure.
      case "wallet_balance":
        return <SkBar h={34} w="52%" radius={6} />;
      // Short bullet lines.
      case "info":
      case "support_ticket":
        return (
          <Box style={{ display: "grid", gap: 8 }}>
            <SkBar w="88%" h={10} />
            <SkBar w="64%" h={10} />
          </Box>
        );
      default:
        return (
          <Box style={{ display: "grid", gap: 8 }}>
            <SkBar w="90%" h={10} />
            <SkBar w="72%" h={10} />
            <SkBar w="80%" h={10} />
          </Box>
        );
    }
  })();

  return (
    <Box
      style={{
        width: "100%",
        border: "1px solid rgba(24,24,27,.08)",
        borderRadius: 8,
        background: "var(--surface-default)",
        boxShadow: "0 10px 24px rgba(15,23,42,.045)",
        overflow: "hidden",
      }}
    >
      <Box style={{ padding: "10px 12px 8px" }}>
        <SkBar w="34%" h={9} />
        <Box mt={7}>
          <SkBar w="58%" h={12} />
        </Box>
      </Box>
      <Divider color="rgba(24,24,27,.07)" />
      <Box style={{ padding: 10 }}>{body}</Box>
    </Box>
  );
}

function SkBar({ w = "100%", h = 10, radius = 5 }: { w?: string | number; h?: number; radius?: number }) {
  return (
    <Box
      style={{
        width: w,
        height: h,
        borderRadius: radius,
        background:
          "linear-gradient(90deg, var(--action-ghost-bg-hover) 25%, rgba(24,24,27,.11) 37%, var(--action-ghost-bg-hover) 63%)",
        backgroundSize: "400% 100%",
        animation: "cwShimmer 1.3s ease-in-out infinite",
      }}
    />
  );
}

/** Titled block: confirmations ("Trip cancelled") and short reference lists. */
function InfoCard({ data }: { data: Record<string, unknown> }) {
  const items = asRows(data.items);
  const tone = displayValue(data.tone) || "neutral";
  const accent =
    tone === "success" ? "var(--success-fg)" : tone === "warning" ? "var(--warning-fg)" : "var(--text-brand)";
  return (
    <CardShell title={displayValue(data.title) || "Details"}>
      {displayValue(data.body) && (
        <Text fz={12.5} c="var(--text-secondary)" lh={1.45} mb={items.length ? 8 : 0}>
          {displayValue(data.body)}
        </Text>
      )}
      {items.map((item, idx) => (
        <Group
          key={`${displayValue(item.label)}-${idx}`}
          justify="space-between"
          wrap="nowrap"
          align="flex-start"
          style={{
            padding: "7px 0",
            borderTop: idx === 0 ? undefined : "1px solid var(--action-ghost-bg-hover)",
          }}
        >
          <Box style={{ minWidth: 0 }}>
            <Text fz={12.5} fw={650} c="var(--text-primary)" lh={1.3}>
              <Text span c={accent} fw={800} mr={6}>
                •
              </Text>
              {displayValue(item.label)}
            </Text>
            {displayValue(item.sub) && (
              <Text fz={11.5} c="var(--text-tertiary)" lh={1.35} mt={2}>
                {displayValue(item.sub)}
              </Text>
            )}
          </Box>
          {displayValue(item.value) && (
            <Text fz={12.5} fw={700} c="var(--text-primary)" style={{ whiteSpace: "nowrap" }}>
              {displayValue(item.value)}
            </Text>
          )}
        </Group>
      ))}
    </CardShell>
  );
}

function SmallMeta({ label, value }: { label?: string; value?: unknown }) {
  const text = displayValue(value);
  if (!text) return null;
  return (
    <Text fz={11.5} c="var(--text-tertiary)" lh={1.35}>
      {label ? `${label}: ` : ""}
      <Text span c="var(--text-secondary)" fw={600}>
        {text}
      </Text>
    </Text>
  );
}

function StatusBadge({ value }: { value?: unknown }) {
  const text = titleCase(value);
  if (!text) return null;
  return (
    <Badge
      size="xs"
      radius="sm"
      color={/active|completed|success|resolved/i.test(text) ? "green" : /pending|open|progress/i.test(text) ? "yellow" : "gray"}
      variant="light"
      styles={{ label: { textTransform: "none" } }}
    >
      {text}
    </Badge>
  );
}

function StatGroupCard({ data }: { data: Record<string, unknown> }) {
  const stats = asRows(data.stats);
  return (
    <CardShell title={displayValue(data.title) || "Overview"} eyebrow={displayValue(data.eyebrow)}>
      <Box
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: 8,
        }}
      >
        {stats.map((stat, idx) => (
          <Box
            key={`${displayValue(stat.label)}-${idx}`}
            style={{
              border: "1px solid rgba(24,24,27,.07)",
              borderRadius: 6,
              padding: "9px 10px",
              background: "var(--surface-canvas)",
              minWidth: 0,
            }}
          >
            <Text fz={11} c="var(--text-tertiary)" lh={1.15} truncate>
              {displayValue(stat.label)}
            </Text>
            <Text fw={800} fz={15} c="var(--text-primary)" lh={1.2} mt={3} truncate>
              {displayValue(stat.value)}
            </Text>
            {displayValue(stat.sub) && (
              <Text fz={10.5} c="var(--text-tertiary)" lh={1.2} mt={2} truncate>
                {displayValue(stat.sub)}
              </Text>
            )}
          </Box>
        ))}
      </Box>
    </CardShell>
  );
}

function PlatformOverviewCard({ data }: { data: Record<string, unknown> }) {
  const hero = asRecord(data.hero);
  const stats = asRows(data.stats);
  const sections = asRows(data.sections);
  return (
    <CardShell
      title={displayValue(data.title) || "Platform overview"}
      eyebrow={displayValue(data.eyebrow) || "Live operations"}
    >
      <Box
        style={{
          padding: "10px 11px",
          border: "1px solid rgba(24,24,27,.08)",
          borderRadius: 8,
          background: "linear-gradient(180deg, var(--surface-default) 0%, var(--surface-canvas) 100%)",
        }}
      >
        <Text fz={11} c="var(--text-tertiary)" lh={1.1}>
          {displayValue(hero.label) || "Primary KPI"}
        </Text>
        <Text fw={850} fz={23} c="var(--text-primary)" lh={1.05} mt={5}>
          {displayValue(hero.value)}
        </Text>
        <Text fz={11.5} c="var(--text-tertiary)" lh={1.2} mt={5}>
          {displayValue(hero.sub)}
        </Text>
      </Box>

      <Box
        mt={8}
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: 6,
        }}
      >
        {stats.slice(0, 6).map((stat, idx) => (
          <Box
            key={`${displayValue(stat.label)}-${idx}`}
            style={{
              minWidth: 0,
              border: "1px solid rgba(24,24,27,.07)",
              borderRadius: 6,
              padding: "8px 8px",
              background: "var(--surface-canvas)",
            }}
          >
            <Text fz={10.5} c="var(--text-tertiary)" lh={1.1} truncate>
              {displayValue(stat.label)}
            </Text>
            <Text fw={820} fz={13.5} c="var(--text-primary)" lh={1.15} mt={4} truncate>
              {displayValue(stat.value)}
            </Text>
            <Text fz={10} c="var(--text-tertiary)" lh={1.1} mt={3} truncate>
              {displayValue(stat.sub)}
            </Text>
          </Box>
        ))}
      </Box>

      <Box mt={9} style={{ display: "flex", flexDirection: "column", gap: 7 }}>
        {sections.slice(0, 4).map((section, idx) => {
          const items = asRows(section.items);
          return (
            <Box
              key={`${displayValue(section.title)}-${idx}`}
              style={{
                border: "1px solid rgba(24,24,27,.07)",
                borderRadius: 6,
                padding: "8px 9px",
                background: "var(--surface-default)",
              }}
            >
              <Text fw={760} fz={11.5} c="var(--text-primary)" lh={1.1}>
                {displayValue(section.title)}
              </Text>
              <Box
                mt={7}
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: "6px 10px",
                }}
              >
                {items.map((item, itemIdx) => (
                  <Group key={itemIdx} justify="space-between" gap={6} wrap="nowrap">
                    <Text fz={10.7} c="var(--text-tertiary)" truncate>
                      {displayValue(item.label)}
                    </Text>
                    <Text fz={11.2} fw={760} c="var(--text-primary)" truncate>
                      {displayValue(item.value)}
                    </Text>
                  </Group>
                ))}
              </Box>
            </Box>
          );
        })}
      </Box>
    </CardShell>
  );
}

function ListCard({
  title,
  rows,
  primaryKeys,
  secondaryKeys,
  rightKeys = [],
}: {
  title: string;
  rows: Record<string, unknown>[];
  primaryKeys: string[];
  secondaryKeys: string[];
  rightKeys?: string[];
}) {
  return (
    <CardShell title={title}>
      <Box style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {rows.slice(0, 6).map((row, idx) => {
          const primary = firstText(row, primaryKeys) || `Item ${idx + 1}`;
          const secondary = secondaryKeys
            .map((key) => firstText(row, [key]))
            .filter(Boolean)
            .slice(0, 2)
            .join(" · ");
          const right = firstText(row, rightKeys);
          return (
            <Box
              key={`${primary}-${idx}`}
              style={{
                border: "1px solid rgba(24,24,27,.07)",
                borderRadius: 8,
                padding: "8px 9px",
                background: "var(--surface-canvas)",
              }}
            >
              <Group justify="space-between" gap={8} wrap="nowrap">
                <Text fw={700} fz={12.5} c="var(--text-primary)" lh={1.25} truncate>
                  {primary}
                </Text>
                {right ? (
                  <Text fw={750} fz={11.5} c="var(--text-primary)" lh={1.2} truncate>
                    {right}
                  </Text>
                ) : (
                  <StatusBadge value={row.status} />
                )}
              </Group>
              {secondary && (
                <Text fz={11.5} c="var(--text-tertiary)" lh={1.35} mt={3} lineClamp={2}>
                  {secondary}
                </Text>
              )}
              <Group gap={8} mt={5}>
                <SmallMeta value={row.date ?? row.joined ?? row.occurredAt} />
                <SmallMeta value={row.payment_mode ?? row.phone ?? row.phoneNumber} />
              </Group>
            </Box>
          );
        })}
      </Box>
    </CardShell>
  );
}

function DataTableCard({ data }: { data: Record<string, unknown> }) {
  const rows = asRows(data.rows);
  return (
    <CardShell title={displayValue(data.title) || "Analytics"}>
      <Box style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {rows.slice(0, 8).map((row, idx) => (
          <Box
            key={idx}
            style={{
              border: "1px solid rgba(24,24,27,.07)",
              borderRadius: 8,
              padding: "8px 9px",
              background: "var(--surface-canvas)",
            }}
          >
            <Group justify="space-between" gap={10} wrap="nowrap">
              <Text fz={12} fw={680} c="var(--text-primary)" truncate>
                {displayValue(row.label) ||
                  displayValue(row.name) ||
                  formatGroupLabel(row._id)}
              </Text>
              <Text fz={12.5} fw={820} c="var(--text-primary)" truncate>
                {analyticsValue(row)}
              </Text>
            </Group>
            {analyticsSub(row) && (
              <Text fz={10.5} c="var(--text-tertiary)" lh={1.2} mt={4} truncate>
                {analyticsSub(row)}
              </Text>
            )}
          </Box>
        ))}
      </Box>
    </CardShell>
  );
}

function FareBreakdownCard({ data }: { data: Record<string, unknown> }) {
  const lines = asRows(data.lines);
  const total = asRecord(data.total);
  return (
    <CardShell title={displayValue(data.title) || "Fare breakdown"}>
      <Box style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {lines.map((line, idx) => (
          <Group key={idx} justify="space-between" gap={8} wrap="nowrap">
            <Text fz={12} c="var(--text-secondary)" truncate>{displayValue(line.label)}</Text>
            <Text fz={12} fw={700} c={line.kind === "credit" ? "green" : "var(--text-primary)"} truncate>
              {displayValue(line.value)}
            </Text>
          </Group>
        ))}
        <Divider my={3} />
        <Group justify="space-between" gap={8} wrap="nowrap">
          <Text fz={12.5} fw={800} c="var(--text-primary)">{displayValue(total.label) || "Total"}</Text>
          <Text fz={13} fw={850} c="var(--text-primary)">{displayValue(total.value)}</Text>
        </Group>
      </Box>
    </CardShell>
  );
}

function GenericCard({ card }: { card: UiCard }) {
  const data = asRecord(card.data);
  const rows = asRows(data.trips ?? data.transactions ?? data.tickets ?? data.items ?? data.users ?? data.alerts);
  return (
    <ListCard
      title={displayValue(data.title) || titleCase(card.type)}
      rows={rows.length ? rows : [data]}
      primaryKeys={["name", "ticket_no", "ref", "id", "status", "item_type"]}
      secondaryKeys={["message", "description", "email", "route", "reason", "passenger", "driver"]}
      rightKeys={["amount", "wallet_balance", "fare", "total"]}
    />
  );
}

function ChatCard({ card }: { card: UiCard }) {
  const data = asRecord(card.data);
  if (card.type === "stat_group" && data.variant === "platform_overview") {
    return <PlatformOverviewCard data={data} />;
  }
  if (card.type === "stat_group") return <StatGroupCard data={data} />;
  if (card.type === "info") return <InfoCard data={data} />;
  if (card.type === "data_table") return <DataTableCard data={data} />;
  if (card.type === "fare_breakdown") return <FareBreakdownCard data={data} />;
  if (card.type === "wallet_balance") {
    return (
      <StatGroupCard
        data={{
          title: "Wallet balance",
          stats: [{ label: "Balance", value: data.balance }],
        }}
      />
    );
  }
  if (card.type === "user_list") {
    return (
      <ListCard
        title={displayValue(data.title) || "Matching accounts"}
        rows={asRows(data.users)}
        primaryKeys={["name", "phone"]}
        secondaryKeys={["email", "status"]}
        rightKeys={["wallet_balance", "rating"]}
      />
    );
  }
  if (card.type === "trip_list") {
    return (
      <ListCard
        title={displayValue(data.title) || "Trips"}
        rows={asRows(data.trips)}
        primaryKeys={["ref", "ride_type", "status"]}
        secondaryKeys={["route", "passenger", "driver", "counterparty"]}
        rightKeys={["amount", "fare"]}
      />
    );
  }
  if (card.type === "ticket_list") {
    return (
      <ListCard
        title={displayValue(data.title) || "Support tickets"}
        rows={asRows(data.tickets)}
        primaryKeys={["ticket_no", "category"]}
        secondaryKeys={["message", "created_by"]}
      />
    );
  }
  if (card.type === "transaction_list") {
    return (
      <ListCard
        title={displayValue(data.title) || "Transactions"}
        rows={asRows(data.transactions)}
        primaryKeys={["ref", "type"]}
        secondaryKeys={["reason", "status"]}
        rightKeys={["amount"]}
      />
    );
  }
  if (card.type === "lost_item_list") {
    return (
      <ListCard
        title={displayValue(data.title) || "Lost & found"}
        rows={asRows(data.items)}
        primaryKeys={["ticket_no", "item_type"]}
        secondaryKeys={["description", "status"]}
      />
    );
  }
  if (card.type === "sos_list") {
    return (
      <ListCard
        title={displayValue(data.title) || "SOS alerts"}
        rows={asRows(data.alerts)}
        primaryKeys={["name", "occurredBy", "phoneNumber"]}
        secondaryKeys={["tripID", "occurredAt"]}
      />
    );
  }
  return <GenericCard card={card} />;
}

function sameCards(a?: UiCard[], b?: UiCard[]) {
  return JSON.stringify(a ?? []) === JSON.stringify(b ?? []);
}

// Cycling "typing…" phrases so a slow turn reads as deliberate work, not a stall.
const TYPING_PHRASES = [
  "Smart Safiri is typing",
  "Thinking it through",
  "Checking the data",
  "Putting it together",
  "Almost ready",
];

function TypingIndicator() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(
      () => setI((p) => (p + 1) % TYPING_PHRASES.length),
      1900,
    );
    return () => clearInterval(t);
  }, []);
  return (
    <span key={i} className="cw-typing-label" role="status" aria-live="polite">
      {TYPING_PHRASES[i]}…
    </span>
  );
}

function quickReplyText(reply: QuickReply): string {
  return typeof reply === "string" ? reply : reply.label || reply.value || "";
}

function quickReplyValue(reply: QuickReply): string {
  return typeof reply === "string" ? reply : reply.value || reply.label || "";
}

export function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([WELCOME]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [threadId, setThreadId] = useState(getStoredThreadId);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = useCallback(() => {
    setTimeout(
      () => bottomRef.current?.scrollIntoView({ behavior: "smooth" }),
      40,
    );
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 180);
  }, [open]);

  async function sendMessage(text: string) {
    const trimmed = text.trim();
    if (!trimmed || loading) return;
    setInput("");

    const userMsg: Message = {
      id: `u-${Date.now()}`,
      role: "user",
      content: trimmed,
    };
    const botId = `b-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      userMsg,
      { id: botId, role: "assistant", content: "", streaming: true },
    ]);
    setLoading(true);

    try {
      const token = getToken();
      const res = await fetch(`${BOT_BASE_URL}/chat/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          message: trimmed,
          ...(threadId ? { thread_id: threadId } : {}),
        }),
      });

      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let quickReplies: string[] = [];
      let cards: UiCard[] = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop() ?? "";

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === "token") {
              if (cards.length > 0) continue;
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === botId
                    ? { ...m, content: m.content + data.content }
                    : m,
                ),
              );
              scrollToBottom();
            } else if (data.type === "pending") {
              // Router has decided. Either shows the matching skeletons, or (empty
              // array) tells us this turn is prose and no skeleton should appear.
              const hinted: string[] = Array.isArray(data.cards) ? data.cards : [];
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === botId ? { ...m, pendingCards: hinted } : m,
                ),
              );
              scrollToBottom();
            } else if (data.type === "cards") {
              cards = Array.isArray(data.ui_actions) ? data.ui_actions : [];
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === botId
                    ? sameCards(m.cards, cards)
                      ? m
                      : { ...m, cards, content: "" }
                    : m,
                ),
              );
              scrollToBottom();
            } else if (data.type === "done") {
              quickReplies = data.quick_replies ?? [];
              cards = Array.isArray(data.ui_actions) ? data.ui_actions : cards;
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === botId
                    ? sameCards(m.cards, cards)
                      ? m
                      : { ...m, cards, content: cards.length > 0 ? "" : m.content }
                    : m,
                ),
              );
              if (data.thread_id) {
                setThreadId(data.thread_id);
                storeThreadId(data.thread_id);
              }
            } else if (data.type === "error") {
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === botId
                    ? {
                        ...m,
                        content: data.message ?? "Something went wrong.",
                        streaming: false,
                      }
                    : m,
                ),
              );
            }
          } catch {
            /* malformed line */
          }
        }
      }

      setMessages((prev) =>
        prev.map((m) =>
          m.id === botId
            ? {
                ...m,
                content: cards.length > 0 ? "" : m.content,
                streaming: false,
                quickReplies,
                cards,
              }
            : m,
        ),
      );
    } catch {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === botId
            ? {
                ...m,
                content: "Could not reach Smart Safiri AI assistant. Please try again.",
                streaming: false,
              }
            : m,
        ),
      );
    } finally {
      setLoading(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  }

  return (
    <>
      <style>{`
        @keyframes _blink   { 0%,100%{opacity:1} 50%{opacity:0} }
        @keyframes _fadeUp  { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        @keyframes _pulse   { 0%,100%{box-shadow:0 0 0 0 rgba(34,197,94,.38)} 50%{box-shadow:0 0 0 6px rgba(34,197,94,0)} }
        @keyframes _shimmer { from{background-position:120% 0} to{background-position:-120% 0} }
        @keyframes _qrIn    { from{opacity:0;transform:translateY(5px)} to{opacity:1;transform:translateY(0)} }
        @keyframes cwShimmer{ 0%{background-position:100% 50%} 100%{background-position:0 50%} }

        .cw-msg { animation: _fadeUp .2s ease both }
        .cw-launcher:hover { background: var(--action-secondary-bg-hover) !important }
        .cw-panel-scroll::-webkit-scrollbar { width: 8px }
        .cw-panel-scroll::-webkit-scrollbar-track { background: transparent }
        .cw-panel-scroll::-webkit-scrollbar-thumb {
          background: rgba(24,24,27,.14);
          border: 2px solid transparent;
          border-radius: 999px;
          background-clip: content-box;
        }

        /* Shimmering "typing" status text — sweeps a dark band across muted text */
        .cw-typing-label {
          font-size: 13px; font-weight: 600; letter-spacing: .1px; white-space: nowrap;
          background: linear-gradient(90deg,
            var(--text-disabled) 0%, var(--text-disabled) 40%, var(--text-primary) 50%, var(--text-disabled) 60%, var(--text-disabled) 100%);
          background-size: 220% 100%;
          -webkit-background-clip: text; background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: _shimmer 1.6s linear infinite, _fadeUp .3s ease both;
        }

        /* Premium flat option buttons (matches the front-end tester) */
        .cw-quick-chip { animation: _qrIn .32s cubic-bezier(.2,.7,.3,1) both }
        .cw-quick-chip:nth-child(2)   { animation-delay: .05s }
        .cw-quick-chip:nth-child(3)   { animation-delay: .10s }
        .cw-quick-chip:nth-child(4)   { animation-delay: .15s }
        .cw-quick-chip:nth-child(n+5) { animation-delay: .20s }
        .cw-quick-chip:hover:not(:disabled) {
          border-color: var(--action-primary-bg);
          background: var(--brand-subtle-bg);
          transform: translateY(-1px);
        }
        .cw-quick-chip:active:not(:disabled) { transform: translateY(1px) }
        .cw-close:hover { background: var(--action-ghost-bg-hover) }

        @media (max-width: 520px) {
          .cw-panel {
            right: 12px !important;
            left: 12px !important;
            bottom: 78px !important;
            width: auto !important;
            height: min(620px, calc(100vh - 104px)) !important;
          }
          .cw-launcher-wrap {
            right: 16px !important;
            bottom: 16px !important;
          }
        }
      `}</style>

      {/* ── Trigger ── */}
      <Box
        className="cw-launcher-wrap"
        style={{ position: "fixed", bottom: 24, right: 24, zIndex: 1100 }}
      >
        <ActionIcon
          className="cw-launcher"
          size={44}
          radius="xl"
          aria-label="Smart Safiri AI assistant"
          onClick={() => setOpen((o) => !o)}
          style={{
            position: "relative",
            // Quiet launcher: raised surface + brand glyph. The oxblood fill is
            // reserved for the page's one primary action.
            background: "var(--surface-raised)",
            color: "var(--text-brand)",
            border: "1px solid var(--border-default)",
            boxShadow: "var(--shadow-raised)",
            transition: "background .15s, transform .15s ease",
          }}
        >
          {open ? <IconX size={18} /> : <SparkMark size={20} />}
        </ActionIcon>
      </Box>

      {/* ── Panel ── */}
      <Box
        className="cw-panel"
        style={{
          position: "fixed",
          bottom: 82,
          right: 24,
          zIndex: 1099,
          width: 410,
          height: 590,
          display: "flex",
          flexDirection: "column",
          opacity: open ? 1 : 0,
          transform: open
            ? "scale(1) translateY(0)"
            : "scale(0.97) translateY(14px)",
          transformOrigin: "bottom right",
          transition:
            "opacity .22s ease, transform .22s ease, visibility .22s ease",
          pointerEvents: open ? "auto" : "none",
          visibility: open ? "visible" : "hidden",
          borderRadius: 14,
          overflow: "hidden",
          border: "1px solid rgba(24,24,27,.09)",
          boxShadow:
            "0 22px 55px rgba(15,23,42,.14), 0 5px 16px rgba(15,23,42,.06), inset 0 1px 0 rgba(255,255,255,.86)",
          background: "var(--surface-default)",
          backdropFilter: "blur(18px)",
        }}
      >
        {/* Header */}
        <Box
          style={{
            padding: "16px 18px 15px",
            borderBottom: "1px solid rgba(24,24,27,.08)",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "var(--surface-default)",
          }}
        >
          <Group gap={12} align="center">
            <Box
              style={{
                width: 38,
                height: 38,
                borderRadius: 12,
                background: "var(--surface-default)",
                border: "1px solid rgba(24,24,27,.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                boxShadow: "0 8px 18px rgba(15,23,42,.06)",
              }}
            >
              <Text
                fw={900}
                fz={17}
                c="var(--text-primary)"
                lh={1}
              >
                S
              </Text>
            </Box>
            <Box>
              <Text fw={750} fz={15} c="var(--text-primary)" lh={1.15}>
                Smart Safiri AI assistant
              </Text>
              <Group gap={6} align="center" mt={5}>
                <Box
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    background: "var(--success-solid)",
                    boxShadow: "0 0 0 3px rgba(34,197,94,.12)",
                  }}
                />
                <Text fz={11.5} c="var(--text-tertiary)" lh={1}>
                  Admin operations assistant
                </Text>
              </Group>
            </Box>
          </Group>

          <ActionIcon
            className="cw-close"
            variant="subtle"
            color="gray"
            size={30}
            radius="xl"
            onClick={() => setOpen(false)}
            style={{ transition: "background .15s ease" }}
          >
            <IconX size={15} />
          </ActionIcon>
        </Box>

        {/* Messages */}
        <Box
          className="cw-panel-scroll"
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "16px 16px 8px",
            display: "flex",
            flexDirection: "column",
            gap: 10,
            background: "var(--surface-canvas)",
          }}
        >
          {messages.map((msg) => (
            <Box key={msg.id} className="cw-msg">
              {/* One skeleton per card the server said is coming, shaped like that
                  card. Nothing renders until the `pending` event arrives, and
                  nothing renders at all when it comes back empty (text-only turn). */}
              {msg.role === "assistant" &&
                msg.streaming &&
                (!msg.cards || msg.cards.length === 0) &&
                (msg.pendingCards?.length ?? 0) > 0 && (
                  <Box
                    style={{
                      width: "92%",
                      maxWidth: "100%",
                      marginBottom: 8,
                      display: "flex",
                      flexDirection: "column",
                      gap: 8,
                    }}
                  >
                    {msg.pendingCards!.map((cardType, index) => (
                      <CardSkeleton key={`${cardType}-${index}`} type={cardType} />
                    ))}
                  </Box>
                )}

              {msg.role === "assistant" && msg.cards && msg.cards.length > 0 && (
                <Box
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                    width: "92%",
                    maxWidth: "100%",
                    marginBottom: msg.content || msg.streaming ? 8 : 0,
                  }}
                >
                  {msg.cards.map((card, index) => (
                    <ChatCard key={`${card.type}-${card.id ?? index}`} card={card} />
                  ))}
                </Box>
              )}

              {/* Bubble. Hidden while skeletons are on screen — the skeleton is
                  already the progress indicator, so a "thinking…" bubble next to it
                  is two spinners for one wait. */}
              {(msg.role === "user" ||
                ((!msg.cards || msg.cards.length === 0) &&
                  (msg.content ||
                    (msg.streaming && !(msg.pendingCards?.length ?? 0))))) && (
                <Box
                  style={{
                    display: "flex",
                    justifyContent:
                      msg.role === "user" ? "flex-end" : "flex-start",
                  }}
                >
                  <Box
                    style={{
                      maxWidth: "83%",
                      padding: "10px 13px",
                      borderRadius:
                        msg.role === "user"
                          ? "11px 4px 11px 11px"
                          : "4px 11px 11px 11px",
                      background:
                        msg.role === "user"
                          ? "var(--action-primary-bg)"
                          : "var(--surface-default)",
                      color: msg.role === "user" ? "#fff" : "var(--text-primary)",
                      fontSize: 13.5,
                      lineHeight: 1.58,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      border:
                        msg.role === "user"
                          ? "1px solid rgba(255,255,255,.16)"
                          : "1px solid rgba(24,24,27,.08)",
                      boxShadow:
                        msg.role === "user"
                          ? "0 10px 22px rgba(230,25,45,.22)"
                          : "0 8px 20px rgba(15,23,42,.06)",
                    }}
                  >
                    {msg.streaming && !msg.content ? (
                      <TypingIndicator />
                    ) : (
                      <>
                        {renderText(msg.content)}
                        {msg.streaming && (
                          <Box
                            component="span"
                            style={{
                              display: "inline-block",
                              width: 1.5,
                              height: "0.85em",
                              background:
                                msg.role === "user" ? "#fff" : "var(--text-tertiary)",
                              marginLeft: 2,
                              verticalAlign: "text-bottom",
                              animation: "_blink .85s step-end infinite",
                            }}
                          />
                        )}
                      </>
                    )}
                  </Box>
                </Box>
              )}

              {/* Quick replies */}
              {msg.quickReplies && msg.quickReplies.length > 0 && (
                <Box
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 5,
                    marginTop: 6,
                  }}
                >
                  {msg.quickReplies.map((qr) => (
                    <Box
                      key={quickReplyValue(qr)}
                      className="cw-quick-chip"
                      component="button"
                      disabled={loading}
                      onClick={() => sendMessage(quickReplyValue(qr))}
                      style={{
                        padding: "8px 13px",
                        borderRadius: 8,
                        border: "1px solid rgba(230,25,45,.28)",
                        background: "var(--surface-default)",
                        fontSize: 12.5,
                        fontWeight: 600,
                        lineHeight: 1.2,
                        color: "var(--text-primary)",
                        cursor: loading ? "not-allowed" : "pointer",
                        opacity: loading ? 0.5 : 1,
                        transition:
                          "border-color .18s, background .18s, transform .14s",
                        fontFamily: "inherit",
                      }}
                    >
                      {quickReplyText(qr)}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          ))}
          <div ref={bottomRef} />
        </Box>

        {/* Input */}
        <Box
          style={{
            padding: "12px 14px 14px",
            borderTop: "1px solid rgba(24,24,27,.08)",
            background: "var(--surface-default)",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <TextInput
            ref={inputRef}
            placeholder="Message Smart Safiri AI assistant..."
            value={input}
            onChange={(e) => setInput(e.currentTarget.value)}
            onKeyDown={handleKeyDown}
            disabled={loading}
            flex={1}
            radius="xl"
            size="sm"
            styles={{
              input: {
                height: 38,
                border: "1px solid rgba(24,24,27,.1)",
                background: "var(--surface-canvas)",
                fontSize: 13.5,
                boxShadow: "inset 0 1px 0 rgba(255,255,255,.9)",
                transition: "border-color .15s ease, box-shadow .15s ease",
              },
            }}
          />
          <ActionIcon
            size={38}
            radius="xl"
            disabled={loading || !input.trim()}
            onClick={() => sendMessage(input)}
            style={{
              background:
                !loading && input.trim()
                  ? "var(--action-primary-bg)"
                  : "var(--action-ghost-bg-hover)",
              color: !loading && input.trim() ? "#fff" : "var(--text-disabled)",
              border: "none",
              flexShrink: 0,
              boxShadow:
                !loading && input.trim()
                  ? "0 10px 22px rgba(230,25,45,.22)"
                  : "none",
              transition: "background .18s, color .18s, box-shadow .18s",
            }}
          >
            {loading ? (
              <Loader size={12} color="gray" type="dots" />
            ) : (
              <IconSend size={14} />
            )}
          </ActionIcon>
        </Box>
      </Box>
    </>
  );
}
